/**
 * Customer Portal API Client
 *
 * Provides access to customer-scoped endpoints: profile, projects,
 * file uploads, and the multipart upload flow (initiate, presign, complete, abort).
 */

import { createApiClient } from '@shared/services/apiBase';
import { customerAuthService } from './auth';
import type {
  Customer,
  Project,
  FileUpload,
  BrandingConfig,
  UploadInitiateResponse,
  PresignedPartUrl,
  UploadPart,
  CustomerSecurityOverview,
} from '@shared/types';

const client = createApiClient({
  getToken: () => customerAuthService.getIdToken(),
  onUnauthorized: () => customerAuthService.logout(),
});

export const customerApiClient = {
  // ---------------------------------------------------------------------------
  // Config (public, no /customer/ prefix)
  // ---------------------------------------------------------------------------

  async getPublicConfig(): Promise<BrandingConfig> {
    const response = await client.get('/config/public');
    return response.data;
  },

  // ---------------------------------------------------------------------------
  // Customer profile
  // ---------------------------------------------------------------------------

  async getMe(): Promise<Customer> {
    const response = await client.get('/customer/me');
    return response.data;
  },

  // ---------------------------------------------------------------------------
  // Projects (customer-scoped)
  // ---------------------------------------------------------------------------

  async getMyProjects(): Promise<Project[]> {
    const response = await client.get('/customer/projects');
    return response.data;
  },

  async getProject(projectId: string): Promise<Project> {
    const response = await client.get(`/customer/projects/${projectId}`);
    return response.data;
  },

  // ---------------------------------------------------------------------------
  // Uploads
  // ---------------------------------------------------------------------------

  async getMyUploads(projectId: string): Promise<FileUpload[]> {
    const response = await client.get(`/customer/projects/${projectId}/uploads`);
    return response.data;
  },

  // ---------------------------------------------------------------------------
  // Multipart upload flow
  // ---------------------------------------------------------------------------

  async initiateUpload(
    projectId: string,
    data: { file_name: string; file_size: number; mime_type: string },
  ): Promise<UploadInitiateResponse> {
    const response = await client.post(
      `/customer/projects/${projectId}/uploads/initiate`,
      data,
    );
    return response.data;
  },

  async presignParts(
    projectId: string,
    uploadSk: string,
    partNumbers: number[],
  ): Promise<{ urls: PresignedPartUrl[] }> {
    const encoded = encodeURIComponent(uploadSk);
    const response = await client.post(
      `/customer/projects/${projectId}/uploads/${encoded}/presign-parts`,
      { part_numbers: partNumbers },
    );
    return response.data;
  },

  async completeUpload(
    projectId: string,
    uploadSk: string,
    parts: UploadPart[],
  ): Promise<FileUpload> {
    const encoded = encodeURIComponent(uploadSk);
    const response = await client.post(
      `/customer/projects/${projectId}/uploads/${encoded}/complete`,
      { parts },
    );
    return response.data;
  },

  async abortUpload(projectId: string, uploadSk: string): Promise<void> {
    const encoded = encodeURIComponent(uploadSk);
    await client.post(
      `/customer/projects/${projectId}/uploads/${encoded}/abort`,
    );
  },

  // Security
  async getSecurityOverview(): Promise<CustomerSecurityOverview> {
    const response = await client.get('/customer/security/overview');
    return response.data;
  },
};
